import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface MyProfile {
  /** `users.id` — the id foreign keys such as `mapped_by_user_id` reference. */
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string | null;
  role: string;
}

/**
 * The signed-in staff member's `users` profile row.
 *
 * `session.user.id` is the auth uid; tables that record "who did this"
 * (`unifi_site_map.mapped_by_user_id`, `auvik_tenant_map.mapped_by_user_id`,
 * `customer_users.invited_by_user_id`) reference `users.id` instead, so any
 * write that stamps an actor needs this id, not the auth uid.
 */
export function useMyProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['admin-my-profile', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<MyProfile | null> => {
      const { data, error } = await supabase
        .from('users')
        .select('id, auth_user_id, email, full_name, role')
        .eq('auth_user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });
}
